import { useMemo, useState } from 'react';
import { parseZonedDateTime } from '@internationalized/date';
import { differenceInMilliseconds } from 'date-fns';
import { InfoBanner, LinkToCase } from '@ui';
import { useAuthContext } from '@lp/core/auth';
import {
  shouldShowLightsSirensBanner,
  validateAccommodationLegs,
  validateDeviceInMainSegments,
  validateDryRunSegmentsTimeConflicts,
  validateMainSegmentsTimeConflicts,
  validateTmdxAvia,
} from '@lp/features/RoutePlanner/utils';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useLogisticsPermissions } from '@lp/features/RoutePlanner/hooks/useLogisticsPermissions';
import { useDonorEta } from '@lp/features/RoutePlanner/hooks/useDonorEta';
import { CaseBanners } from '@lp/features/CaseOverview/components/CaseBanners';
import { CaseDeviceTrialBanners } from '@lp/features/CaseDeviceTrialBanners/CaseDeviceTrialBanners';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import {
  LEGACY_GROUND_APPROVE_DISABLED_MESSAGE,
  TMDX_DRIVE_GROUND_APPROVE_DISABLED_MESSAGE,
} from '@lp/features/RoutePlanner/config';
import { QuoteStatus, ReviewStatus, ReviewType, RoutePlanStatus } from '@/api/apis/case';
import { RouteBuilderPermissions } from '@/permissions';
import { convertToTimezoneOffset, getTimeZonedDate, getTzDateTimeString } from '@/utils/helpers';
import { RouteTypeEnum } from '@/api/apis/route';
import { OPERATION_TIME_BY_ORGAN } from '@/utils/route';
import QuoteRejectedInfo from './QuoteRejectedInfo';
import { FixedPriceBanner } from './FixedPriceBanner';
import { AircraftUtilizationBanners } from './AircraftUtilizationBanners';

type RouteBannersProps = {
  isSameDH: boolean;
  isSameTX: boolean;
};

export const RouteBanners = ({ isSameDH, isSameTX }: RouteBannersProps) => {
  const { checkPermission } = useAuthContext();
  const { isGroundApprove, isConfirmGroundDisabled, isTmdxDriveCompatible } = useLogisticsPermissions();
  const [showRejectInfo, setShowRejectInfo] = useState<boolean>(true);
  const {
    quote,
    reAllocationCaseId,
    reviews,
    routePlanningStatus,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    organType,
    opoRequest,
    isExpeditedCase,
    donorHospitalDetails,
    txCenterDetails,
    reAllocatedToCaseId,
    isLightsSirensRequired,
    resources,
    trialProgram,
    useFixedPriceFlow,
    nonStopFlightRequired,
  } = useCaseDetailsWithReviews();

  const { Aircraft: aircrafts } = useRouteResources();
  const { legs: mainLegs, accommodationLegs: mainAccommodationLegs } = useRouteDetails(RouteTypeEnum.Main);
  const { legs: dryRunLegs, accommodationLegs: dryRunAccommodationLegs } = useRouteDetails(RouteTypeEnum.DryRun);

  const { offsetInMs } = useDonorEta(mainLegs);

  const sortedReviews = useMemo(() => {
    return reviews.toSorted((a, b) => {
      const aReviewedAt = parseZonedDateTime(getTzDateTimeString(a.reviewedAt)).toAbsoluteString();
      const bReviewedAt = parseZonedDateTime(getTzDateTimeString(b.reviewedAt)).toAbsoluteString();

      return differenceInMilliseconds(new Date(aReviewedAt), new Date(bReviewedAt)) > 0 ? -1 : 1;
    });
  }, [reviews]);

  const aviationDeclineComment = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.LegCreate);
    const isRouteStart = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const aviationReview = sortedReviews.find((r) => r.type === ReviewType.Aviation);

    const isRejectedAvia = aviationReview?.status && aviationReview.status === ReviewStatus.Rejected;
    const showMessage = hasPermission && isRouteStart && isRejectedAvia;

    const comment = aviationReview?.comment ? `Aviation declined: ${aviationReview.comment}` : 'Aviation declined';

    if (showMessage) {
      return comment;
    }

    return '';
  }, [checkPermission, sortedReviews, routePlanningStatus]);

  const rejectedInternallyQuoteComment = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.LegCreate);
    const quoteReview = sortedReviews.find((r) => r.type === ReviewType.Quote);

    if (quoteReview?.status === ReviewStatus.Rejected) {
      const comment = quoteReview?.comment
        ? `Quote declined internally. ${quoteReview.comment}`
        : 'Quote declined internally.';
      return hasPermission && comment;
    }

    return '';
  }, [checkPermission, sortedReviews]);

  const tmdxAviationWarning = useMemo(() => {
    return validateTmdxAvia([...mainLegs, ...dryRunLegs], aircrafts || []);
  }, [aircrafts, dryRunLegs, mainLegs]);

  const routeValidationWarnings = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.LegCreate);

    let warnings: string[] = [];

    if (!hasPermission) {
      return warnings;
    }

    const isCaseWithoutTx = (opoRequest || !!isExpeditedCase) && !txCenterDetails;
    const donorOrTz = getTimeZonedDate(
      donorHospitalDate,
      donorHospitalTime,
      donorHospitalTimeZone || donorHospitalDetails!.location.timezone,
    );
    const donorOrUtc = donorOrTz!.toAbsoluteString();
    const donorEndOperationUtc = donorOrTz!
      .add({ milliseconds: OPERATION_TIME_BY_ORGAN[organType] })
      .toAbsoluteString();

    const isMainTimeValid = validateMainSegmentsTimeConflicts({
      legs: mainLegs,
      donorOrTime: donorOrUtc,
      afterOperationTime: donorEndOperationUtc,
      isCaseWithoutTx,
      isReallocationCase: !!reAllocatedToCaseId,
      donorHospitalId: donorHospitalDetails?.id,
    });

    if (mainLegs.length && !isMainTimeValid) {
      warnings = [...warnings, 'The departure and arrival time of some main legs conflicts and has to be updated.'];
    }

    const isDryRunTimeValid = validateDryRunSegmentsTimeConflicts(dryRunLegs, donorOrUtc, donorHospitalDetails?.id);

    if (dryRunLegs.length && !isDryRunTimeValid) {
      warnings = [...warnings, 'The departure and arrival time of some dry run legs conflicts and has to be updated.'];
    }

    const isMainSegmentWithDevice = validateDeviceInMainSegments(mainLegs);

    if (mainLegs.length && !isMainSegmentWithDevice) {
      warnings = [...warnings, 'OCS device is missing in main segments.'];
    }

    const isDryRunSegmentWithDevice = validateDeviceInMainSegments(dryRunLegs, true);

    if (dryRunLegs.length && !isDryRunSegmentWithDevice) {
      warnings = [...warnings, 'OCS device is missing in dry run segment.'];
    }

    const isMainAccommodationsWithData = validateAccommodationLegs(mainAccommodationLegs);
    const isDryRunAccommodationsWithData = validateAccommodationLegs(dryRunAccommodationLegs);

    if (!(isMainAccommodationsWithData && isDryRunAccommodationsWithData)) {
      warnings = [...warnings, 'The accommodation data is missing.'];
    }

    if (isGroundApprove && isConfirmGroundDisabled) {
      const massage = isTmdxDriveCompatible
        ? TMDX_DRIVE_GROUND_APPROVE_DISABLED_MESSAGE
        : LEGACY_GROUND_APPROVE_DISABLED_MESSAGE;

      warnings = [...warnings, massage];
    }

    return warnings;
  }, [
    donorHospitalDetails,
    checkPermission,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    dryRunLegs,
    mainLegs,
    opoRequest,
    isExpeditedCase,
    organType,
    reAllocatedToCaseId,
    txCenterDetails,
    isConfirmGroundDisabled,
    isGroundApprove,
    mainAccommodationLegs,
    dryRunAccommodationLegs,
    isTmdxDriveCompatible,
  ]);

  return (
    <div className="flex flex-col gap-2 w-full has-[div]:mt-3">
      <CaseBanners />
      {reAllocationCaseId && (
        <InfoBanner>
          Reallocation from <LinkToCase id={reAllocationCaseId} />
        </InfoBanner>
      )}
      {isExpeditedCase && (
        <InfoBanner iconType="info">
          <span className="text-m-sb">Expedited case:</span> the team may be assigned before the logistics is fully
          approved, due to the short window before the OR time.
        </InfoBanner>
      )}
      {opoRequest && <InfoBanner>OPO request</InfoBanner>}
      <CaseDeviceTrialBanners caseTrialProgram={trialProgram} resources={resources} />
      <AircraftUtilizationBanners />
      {!tmdxAviationWarning.isValid && <InfoBanner iconType="warning">{tmdxAviationWarning.errorMessage}</InfoBanner>}
      {quote?.status === QuoteStatus.NoQuote && !!rejectedInternallyQuoteComment && (
        <InfoBanner iconType="cross">{rejectedInternallyQuoteComment}</InfoBanner>
      )}
      {quote && quote.status === QuoteStatus.Rejected && quote.rejectReason && showRejectInfo && (
        <div>
          <QuoteRejectedInfo reason={quote.rejectReason} onClose={() => setShowRejectInfo(false)} />
        </div>
      )}
      {quote && quote.status === QuoteStatus.Recalled && quote.recallComment && (
        <InfoBanner iconType="cross" withClose>
          <span className="whitespace-pre-wrap break-all">{`Quote Recalled. ${quote.recallComment.comment}`}</span>
        </InfoBanner>
      )}
      {!!aviationDeclineComment && <InfoBanner iconType="error">{aviationDeclineComment}</InfoBanner>}
      {!!offsetInMs && (
        <InfoBanner iconType="redWarning">
          {`The ETA to Donor Hospital is ${convertToTimezoneOffset(offsetInMs, false)}h after current OR time.`}
        </InfoBanner>
      )}
      {!isSameDH && <InfoBanner>Donor Hospital has been updated. Some legs have to be updated</InfoBanner>}
      {!isSameTX && <InfoBanner>TX Center has been updated. Some legs have to be updated</InfoBanner>}
      {shouldShowLightsSirensBanner(quote) && (
        <InfoBanner>
          Requesting or removing the Lights and Sirens after the quote generation shall not affect the quote amount
        </InfoBanner>
      )}
      {isLightsSirensRequired && <InfoBanner>Lights and sirens were requested for TXC ground leg</InfoBanner>}
      {routeValidationWarnings.map((s) => (
        <InfoBanner key={s} iconType="warning">
          {s}
        </InfoBanner>
      ))}
      <FixedPriceBanner
        txCenterDetails={txCenterDetails}
        useFixedPriceFlow={useFixedPriceFlow}
        nonStopFlightRequired={nonStopFlightRequired}
      />
    </div>
  );
};
